export const CORE_TUNING = {
  boardSize: 10,
  schemaVersion: 1,
  maxLevel: 20,
  startLives: 3,
  maxLives: 999999,
  startRotationKeys: 1,
  maxRotationKeys: 3,
  extraLifeStep: 250000,
  keyEarnStep: 10000,
  baseBpm: 168,
  warningBeats: 2,
  beatWindowSec: 0.25,
  beatBonusFactor: 0.3,
  globalLeaderboardLimit: 20,
  localLeaderboardLimit: 20,
  doomClockStartSec: 30,
  doomClockDrainPerSec: 1,
  doomClockDrainPerLevel: 0,
  doomClockMatchRewardSec: 3,
  doomClockBomb3BonusSec: 0.3,
  doomClockBomb4BonusSec: 0.4,
  doomClockBomb5BonusSec: 0.5,
  doomClockWipeUseBonusSec: 0.5,
  doomClockWipeMatchedTileBonusSec: 0.2,
  doomClockNoMovesPenaltySec: 5,
  doomClockMaxSec: 30,
  doomClockGrooveChargeBaselineSec: 30,
  doomClockChainBonusSec: 1.0,
  doomClockPowerupSweepBonusSec: 2,
  grooveBombTileBonusFrac: 0.002,
  grooveWipeMatchedTileBonusFrac: 0.003
} as const;

export const CANVAS_TUNING = {
  width: 360,
  height: 600,
  boardPixelSize: 320,
  boardOriginX: 38,
  boardOriginY: 130
} as const;

export const LEVEL_SCORE_TARGETS = [
  18000, 20000, 22000, 24000, 26000,
  28000, 30000, 32000, 34000, 36000,
  34000, 36000, 38000, 40000, 42000,
  40000, 42000, 44000, 46000, 48000
] as const;

export const VERSUS_LEVEL_SCORE_TARGETS = [
  10000, 11000, 12000, 13000, 14000,
  15000, 16000, 17000, 18000, 19000,
  18000, 19000, 20000, 21000, 22000,
  21000, 22000, 23000, 24000, 25000
] as const;

const LEVEL_ROTATION_BASELINE_SECONDS = 22;
const LEVEL_ROTATION_SPEED_MULTIPLIERS = [
  1, 1, 1.1, 1.1, 1.2,
  1.2, 1.3, 1.3, 1.4, 1.4,
  1.5, 1.5, 1.6, 1.6, 1.7,
  1.7, 1.8, 1.8, 1.9, 2
] as const;

export const LEVEL_ROTATION_SECONDS = Object.freeze(
  LEVEL_ROTATION_SPEED_MULTIPLIERS.map(
    (multiplier) => LEVEL_ROTATION_BASELINE_SECONDS / multiplier
  )
);

export const LEVEL_DOOM_CLOCK_CAPS = [
  20, 20, 19, 19, 18,
  18, 17, 17, 16, 15,
  15, 14, 14, 13, 13,
  12, 12, 11, 11, 10
] as const;

export const LEVEL_TILE_TYPES = [
  3, 3, 3, 4, 4, 4, 5, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 11, 12
] as const;

export const LEVEL_PACING_TUNING = {
  rotationSlowdownFactor: 10,
  crateBasePct: 0.035,
  cratePerTierPct: 0.012,
  lockBasePct: 0.028,
  lockPerTierPct: 0.009,
  steelBasePct: 0.004,
  steelPerTierPct: 0.004,
  maxCratePct: 0.16,
  maxLockPct: 0.11,
  maxSteelPct: 0.035
} as const;

export const STATE_MACHINE_TUNING = {
  messageDurationSec: 1.4,
  continueMessageDurationSec: 1.2,
  levelUpPhaseDurationSec: 1.6,
  levelUpNewColorDurationSec: 2.2,
  rotationAnimation: {
    gridSpinSec: 1.0,
    sidewaysHoldSec: 0.25,
    tileRecoverSec: 0.5,
    zoomPeakScale: 1.2
  },
  collapseAnimation: {
    tier3Sec: 0.36,
    tier4Sec: 0.48,
    tier5Sec: 0.62,
    cascadePitchStep: 0.18,
    collapseLandPitchBase: 0.8,
    collapseLandPitchStep: 0.1,
    firstStepTier4Flash: 0.08,
    firstStepTier4Shake: 0.8,
    firstStepTier5Flash: 0.16,
    firstStepTier5Shake: 1.8
  },
  swapAnimation: {
    validSec: 0.12,
    rejectedSec: 0.18
  },
  chainFreeze: {
    chain3PlusSec: 0.04,
    specialSwapSec: 0.06
  },
  displayScoreLerp: {
    perPointMultiplier: 8,
    minSpeed: 1
  },
  flashDecayPerSec: 1.4,
  pulseFx: {
    genericFlash: 0.08,
    genericShake: 1.2,
    extraLifeFlash: 0.1,
    extraLifeShake: 1.0,
    noMoveFlash: 0.18,
    noMoveShake: 2.4,
    levelUpFlash: 0.12,
    levelUpShake: 1.6,
    rotationFlash: 0.1,
    rotationShake: 1.6,
    specialSwapFlash: 0.08,
    specialSwapShake: 0.8
  },
  collapsePulseByTier: {
    tier3FlashBase: 0.06,
    tier3ShakeBase: 0.6,
    tier4FlashBase: 0.14,
    tier4ShakeBase: 1.6,
    tier5FlashBase: 0.22,
    tier5ShakeBase: 2.8,
    flashPerDepth: 0.03,
    shakePerDepth: 0.3
  },
  hypeDurationsSec: {
    nice: 0.7,
    great: 0.8,
    amazing: 0.9,
    incredible: 1.0,
    clutch: 1.05,
    payoff: 1.1,
    grooveReady: 1.2,
    groove: 0.9,
    perfectBeat: 0.45
  },
  versusFeedback: {
    hypeCooldownSec: {
      full: 0.5,
      reduced: 1.05,
      off: 1.4
    },
    significanceScore: {
      full: 200,
      reduced: 280,
      off: 420
    },
    clutchDoomThresholdSec: 8,
    clutchSwingScore: {
      full: 220,
      reduced: 300,
      off: 380
    },
    clutchCooldownSec: 2.4,
    payoffScore: {
      full: 1200,
      reduced: 1800,
      off: 2600
    },
    payoffCooldownSec: 2.8
  }
} as const;

export const FX_TILE_RGB = [
  "92,196,255", "255,122,89", "255,209,102", "126,224,127",
  "204,120,255", "255,79,154", "80,214,188", "255,159,67",
  "85,239,196", "116,185,255", "255,234,167", "162,155,254"
] as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function obstaclePercentsForLevel(level: number): {
  cratePct: number;
  lockPct: number;
  steelPct: number;
} {
  if (level <= 1) {
    return { cratePct: 0, lockPct: 0, steelPct: 0 };
  }

  const tier = Math.floor(level / 2);
  const cratePct = clamp(
    LEVEL_PACING_TUNING.crateBasePct + LEVEL_PACING_TUNING.cratePerTierPct * (tier - 1),
    0,
    LEVEL_PACING_TUNING.maxCratePct
  );
  const lockPct = clamp(
    LEVEL_PACING_TUNING.lockBasePct + LEVEL_PACING_TUNING.lockPerTierPct * (tier - 1),
    0,
    LEVEL_PACING_TUNING.maxLockPct
  );
  const steelPct = tier < 2
    ? 0
    : clamp(
        LEVEL_PACING_TUNING.steelBasePct + LEVEL_PACING_TUNING.steelPerTierPct * (tier - 2),
        0,
        LEVEL_PACING_TUNING.maxSteelPct
      );

  return { cratePct, lockPct, steelPct };
}

export function scrambleOpsForLevel(level: number): number {
  return level + Math.floor(level / 6);
}
